import { AlertPosition } from "@components/alert/Alert.types";
import { WithAlert, withAlert } from "@components/alert/withAlert";
import { WithConfirmationDialog, withConfirmationDialog } from "@components/dialog/withConfirmationDialog";
import { fileNameToMime } from "@components/fileUploader/File.utils";
import { FileToolbarItem, getFileViewer, IFileViewerProps } from "@components/fileViewers/FileViewers.utils";
import { ISelectionChangeArgs } from "@components/inputs/select/Select.types";
import { ISmartODataTableAPI } from "@components/smart/smartTable/SmartODataTableBase";
import { IInboxFileEntity } from "@odata/GeneratedEntityTypes";
import { ApprovalStatusTypeCode, InboxFileSourceCode } from "@odata/GeneratedEnums";
import { IChatData } from "@pages/chats/Chats.utils";
import { ChatDialog } from "@pages/chats/PlainChat.utils";
import { memoizeOne } from "@utils/memoizeOne";
import React from "react";

import BusyIndicator from "../../components/busyIndicator";
import { FILES_API_URL } from "../../constants";
import { Status } from "../../enums";
import { TableStorage } from "../../model/TableStorage";
import BindingContext from "../../odata/BindingContext";
import FileRenameWriteLine from "../../views/fileView/FileRenameWriteLine";
import { currentUserIsCustomer } from "../admin/users/Users.utils";
import EmailBodyMessage from "./EmailBodyMessage";
import {
    createInboxFileMessage,
    handleDeleteFile,
    handleRenameInboxFile,
    IInboxTableCustomData,
    InboxFilter,
    loadInboxFileDetail
} from "./Inbox.utils";
import InboxChat from "./InboxChat";
import InboxFileDetailLayout from "./InboxFileDetailLayout";

export interface IProps extends WithConfirmationDialog, WithAlert {
    storage: TableStorage<ISmartODataTableAPI, IInboxTableCustomData>;
    bindingContext: BindingContext;
    onAfterRemoveFiles?: (bcs: BindingContext[]) => void;
}

interface IState {
    fileData: IInboxFileEntity;
}


class InboxFileDetail extends React.PureComponent<IProps, IState> {
    constructor(props: IProps) {
        super(props);

        this.loadFileDetail();
    }

    componentDidUpdate(prevProps: IProps) {
        if (prevProps.bindingContext?.getKey() !== this.props.bindingContext?.getKey()) {
            this.loadFileDetail();
        }
    }

    _chatData: IChatData;

    get type(): InboxFilter {
        return this.props.storage.getCustomData().type;
    }

    get inboxFile(): IInboxFileEntity {
        return this.state?.fileData;
    }

    get selectedFileHasContents(): boolean {
        return !!this.inboxFile?.FileMetadata?.Id;
    }

    currentUserIsCustomer = memoizeOne(() => {
        return currentUserIsCustomer(this.props.storage.context);
    });

    loadFileDetail = async (): Promise<void> => {
        const fileData = await this.getFileDetail();
        this.setState({ fileData });
    };

    handleSelectedFileRemove = async (): Promise<void> => {
        const bc = this.props.bindingContext;
        if (await handleDeleteFile(this.props.storage, this.props.confirmationDialog, bc)) {
            this.props.onAfterRemoveFiles?.([bc]);
        }
    };

    // inboxFileData enhanced with detail information, communication, etc...
    getFileDetail(): Promise<IInboxFileEntity> {
        return loadInboxFileDetail(this.props.bindingContext, this.props.storage.oData);
    }

    handleChatCreate = (args: ISelectionChangeArgs) => {
        this._chatData = {
            user: args?.value ? {
                id: args?.value as string,
                FirstName: args?.additionalData?.FirstName,
                LastName: args?.additionalData?.LastName,
            } : null,
            isOpen: true
        };

        this.forceUpdate();
    }


    getFileViewer = memoizeOne((): React.ReactElement => {
        const mime = this.inboxFile.FileMetadata?.Name && fileNameToMime(this.inboxFile.FileMetadata.Name);
        const src = this.inboxFile.FileMetadata?.Id && `${FILES_API_URL}/${this.inboxFile.FileMetadata?.Id}`;
        const fileName = this.inboxFile.FileMetadata?.Name;
        const hiddenItems = [FileToolbarItem.Dashboard];
        if (!src) {
            hiddenItems.push(FileToolbarItem.Download);
        }

        const props: IFileViewerProps = {
            src,
            fileName,
            hiddenItems,
            isReadOnly: false,
            onFileRemove: this.handleSelectedFileRemove,
            forcedVisibleItems: [FileToolbarItem.Chat],
            context: this.props.storage.context,
            onChatCreate: this.handleChatCreate,
            id: this.inboxFile.Id
        };

        return (
            getFileViewer(mime, props)
        );
    }, () => [this.inboxFile]);

    handleFileNameChangeConfirm = async (newName: string): Promise<boolean> => {
        if (!newName) {
            return false;
        }

        if (await handleRenameInboxFile(this.props.storage, this.props.bindingContext, newName)) {
            this.inboxFile.FileMetadata.Name = newName;
            return true;
        }
        return false;
    };

    handleCreateMessage = async (message: string, status?: ApprovalStatusTypeCode): Promise<boolean> => {
        const { storage, bindingContext } = this.props;
        const newStatus = (status ?? this.state.fileData.ApprovalStatusTypeCode) as ApprovalStatusTypeCode;
        const result = await createInboxFileMessage(storage.oData, bindingContext, message, newStatus);
        if (!result) {
            // show error message
            // todo: ...
            this.props.setAlert({
                status: Status.Error,
                title: this.props.storage.t("Common:General.Error")
            });
            return false;
        }
        const promises = [storage.tableAPI.reloadTable()];
        if (status && [ApprovalStatusTypeCode.Approved, ApprovalStatusTypeCode.Rejected].includes(status)) {
            this.props.onAfterRemoveFiles([bindingContext]);
        } else {
            await this.loadFileDetail();
        }
        await Promise.all(promises);
        return true;
    };

    renderFileNameChange(): React.ReactNode {
        return (
            <FileRenameWriteLine name={this.inboxFile.FileMetadata.Name}
                                 onChange={this.handleFileNameChangeConfirm}/>
        );
    }

    handleChatClose = () => {
        delete this._chatData;
        this.forceUpdate();
    };

    renderChatDialog() {
        return <ChatDialog isInbox
                           storage={this.props.storage}
                           user={this._chatData?.user}
                           id={this.inboxFile?.Id}
                           inboxName={this.inboxFile?.FileMetadata?.Name}
                           isOpen={this._chatData?.isOpen}
                           onClose={this.handleChatClose}/>;
    }

    render() {
        const isUnsorted = this.type === InboxFilter.Unsorted;

        if (!this.inboxFile) {
            return (
                <BusyIndicator/>
            );
        }

        const showEmailText = this.inboxFile.InboxFileSourceCode === InboxFileSourceCode.Email;

        return (<>
            <InboxFileDetailLayout>
                {(width: number, height: number) => width && height && (<>
                    {this.selectedFileHasContents && this.renderFileNameChange()}
                    {this.getFileViewer()}
                    {isUnsorted
                        ? (showEmailText && <EmailBodyMessage text={this.inboxFile.Body}/>)
                        : <InboxChat file={this.inboxFile}
                                     showEmailText={showEmailText}
                                     isAgendaCustomer={this.currentUserIsCustomer()}
                                     maxHeight={height / 2}
                                     onCreateMessage={this.handleCreateMessage}/>
                    }
                </>)}
            </InboxFileDetailLayout>
            {this.props.alert}
            {this.renderChatDialog()}
        </>);
    }
}

export default withAlert({
    autoHide: true,
    position: AlertPosition.CenteredBottom
})(withConfirmationDialog(InboxFileDetail));
